import { LocalDateTime, Travel } from "@gen/travel/v1/common_pb";
import { Button, i18n, Icon } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { routes } from "@routes";
import { DAP } from "@gen/dap/v1/dap_pb";
import { useEffect, useState } from "react";
import { getDAP } from "@services/dap";
import { localDateTimeToDate } from "@utils/date";
import { getTravel } from "@services/travel";
import { hasFeedbackConsent, trackFeedback } from "@utils/formbricks";

export function Confirmation() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [dap, setDap] = useState<DAP>();

  const [travelFromQueryParams, setTravelFromQueryParams] = useState<
    Travel | undefined
  >();
  const locationStateTravel: Travel | undefined = state?.travel;

  async function fetchTravel(id: string) {
    try {
      const travelResponse = await getTravel(id);
      setTravelFromQueryParams(travelResponse.travel);
      if (!travelResponse.travel) {
        console.error("travel not found");
      }
    } catch (error) {
      console.error("Error fetching travel:", error);
    }
  }

  useEffect(() => {
    const travelId = searchParams.get("travelID");
    if (travelId) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      fetchTravel(travelId);
    }
  }, [searchParams]);

  // No payment, direct validation = travel comes from state
  // Payment = travel comes from query params (travelID)
  const travel = travelFromQueryParams || locationStateTravel;

  const context: "initial" | "exchange" | "payment" = state
    ? state.context
    : "payment";
  const from =
    travel?.outward?.route?.origin?.name ||
    searchParams.get("outwardOriginPlace");
  const to =
    travel?.outward?.route?.destination?.name ||
    searchParams.get("outwardDestinationPlace");
  const outwardDate =
    travel?.outward?.schedule?.departure || searchParams.get("outwardDate");
  const inwardDate =
    travel?.inward?.schedule?.arrival || searchParams.get("inwardDate");

  async function fetchDap() {
    if (!travel) return;
    try {
      const beneficiary = travel.passengers[0];
      const dap = await getDAP(travel.dapNumber, beneficiary.lastName);
      setDap(dap.dap);
    } catch (error) {
      console.error("Error fetching DAP:", error);
    }
  }

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchDap();
  }, [travel]);

  if (!from || !to || !outwardDate || !inwardDate) return;

  const isSuccess = true;

  function getTitle() {
    switch (context) {
      case "payment":
        return t("confirmation.paymentTitle");
      case "exchange":
        return t("confirmation.exchangeTitle");
      case "initial":
      default:
        return t("confirmation.title");
    }
  }

  function getDescription() {
    switch (context) {
      case "payment":
        return t("confirmation.paymentSuccessMessage");
      case "exchange":
        return t("confirmation.exchangeSuccessMessage");
      case "initial":
      default:
        return t("confirmation.successMessage");
    }
  }

  const env = import.meta.env.VITE_APP_ENV;

  function formatDate(date: LocalDateTime | string) {
    if (typeof date === "string") {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(date));
    }
    return Intl.DateTimeFormat(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(localDateTimeToDate(date));
  }

  return (
    <div className="p-12 font-jakarta block-section mb-16 flex flex-col gap-10 m-auto max-w-[700px]">
      {isSuccess ? (
        <>
          <div className="flex flex-col gap-4 justify-center items-center">
            <Icon size={64} color="#008575" src="check_circle_line" />
            <div className="text-text-strong text-[32px] font-bold text-center">
              {getTitle()}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <p className="text-base text-text-strong text-center">
              {getDescription()}
            </p>

            <div className="text-sm text-text-strong text-center">
              <p className="font-bold">
                {t("cart.outward")} : {from} {t("cart.to")} {to}
              </p>
              <p>{formatDate(outwardDate)}</p>
            </div>

            <div className="text-sm text-text-strong text-center">
              <p className="font-bold">
                {t("cart.return")} : {to} {t("cart.to")} {from}
              </p>
              <p>{formatDate(inwardDate)}</p>
            </div>
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:justify-center m-auto w-full max-w-[550px]">
            <Button
              kind="outline"
              size="compact"
              startEnhancer={<Icon src="add_line" color="#0C419A" />}
              onClick={() => navigate(`${routes.SEARCH}`)}
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    outline: `${$theme.colors.blue} solid`,
                    color: $theme.colors.blue,
                    backgroundColor: "transparent",
                    fontWeight: "bold",
                    flex: 1,
                    ":hover": {
                      backgroundColor: "rgba(12, 65, 154, 0.05)",
                    },
                  }),
                },
              }}
            >
              {t("confirmation.bookNewTrip")}
            </Button>

            <Button
              size="compact"
              onClick={() => navigate(`${routes.USER_BOOKINGS}`)}
              overrides={{
                BaseButton: {
                  style: ({ $theme }) => ({
                    outline: `${$theme.colors.blue} solid`,
                    flex: 1,
                  }),
                },
              }}
              endEnhancer={<Icon src="arrow_right_line" color="white" />}
            >
              {t("confirmation.viewBookings")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-4 justify-center items-center">
            <Icon size={64} color="#BD0039" src="warning_line" />
            <div className="text-text-strong text-[32px] font-bold">
              {t("confirmation.errorTitle")}
            </div>
          </div>
          <p className="text-base text-text-strong text-center">
            {t("confirmation.errorMessage")}
          </p>
          <Button
            alignself="center"
            size="compact"
            onClick={() => navigate(`${routes.HOME}`)}
            endEnhancer={<Icon src="arrow_right_line" color="white" />}
          >
            {t("confirmation.backHome")}
          </Button>
        </>
      )}

      {hasFeedbackConsent() && (
        <>
          <div className="text-text-strong text-[24px] font-bold mt-4 text-center">
            {t("satisfaction.title")}
          </div>
          <Button
            alignself="center"
            size="compact"
            onClick={() =>
              trackFeedback({
                env,
                caisse: dap?.caisseId?.toString() ?? "",
                agence: travel?.agencyInfo?.id?.toString() ?? "",
              })
            }
          >
            {t("satisfaction.button")}
          </Button>
        </>
      )}
    </div>
  );
}
